import { AvailableSlots, InterviewFormData, Schedule } from '@/types/forms'

// Helper function to format time
const formatTime = (time: string): string => {
    return new Date(`2024-01-01T${time}`).toLocaleTimeString('es-CO', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    })
}

// Update available hours when date or modality changes
interface ResponseUpdateAvailableHours {
    label: string
    value: string
}
export const UpdateAvailableHours = (
    selectedDate: string,
    selectedModality: string,
    data: AvailableSlots
): ResponseUpdateAvailableHours[] => {
    // Convert selectedDate to required format YYYY-MM-DD
    const formattedDate = new Date(selectedDate).toISOString().split('T')[0]

    // Match the modality case with the availableSlots structure
    const modalityKey =
        selectedModality.toLowerCase() === 'presencial' ? 'onSite' : 'virtual'

    const slots = data[modalityKey]

    const filteredHours = slots
        .filter(
            (slot: Schedule) => slot.day === formattedDate && slot.available
        )
        .map((slot: Schedule) => ({
            label: formatTime(slot.hour),
            value: slot.hour
        }))

    return filteredHours
}

// Helper function to get unique available dates
export const getAvailableDates = (slots: AvailableSlots): string[] => {
    const dates = new Set<string>()

    slots.onSite.forEach((slot) => dates.add(slot.day))
    slots.virtual.forEach((slot) => dates.add(slot.day))

    return Array.from(dates)
}

export const parseDate = (appointmentDate: string): string => {
    return new Date(
        new Date(appointmentDate).getTime() +
            new Date(appointmentDate).getTimezoneOffset() * 60000
    ).toLocaleDateString()
}
interface ResponseValidateDataForm {
    appointmentDate: string
    modalityInterview: string
    hour: number
}
export const validateDataForm = (
    response: InterviewFormData
): undefined | ResponseValidateDataForm => {
    if (
        response.appointmentDate !== null &&
        response.modalityInterview !== null &&
        response.hour !== null
    ) {
        return {
            appointmentDate: response.appointmentDate,
            modalityInterview: response.modalityInterview,
            hour: response.hour
        }
    } else {
        return undefined
    }
}

// Tipo de entrevista según la modalidad final del programa (educationModality)
export type InterviewType = 'virtual' | 'onSite' | 'both' | 'unknown'

const VIRTUAL_MODALITIES = [
    5, // Especialización Virtual
    9, // Maestría Virtual
    16 // Pregrado virtual
]

const ONSITE_MODALITIES = [
    2, // Doctorado presencial
    4, // Especialización Presencial
    8, // Maestría Presencial
    15, // Pregrado presencial
    28 // Presen Virt
]

const BOTH_MODALITIES = [
    1 // Convenio Regional
]

export const getInterviewType = (
    educationModality: string | number | null | undefined
): InterviewType => {
    const modalityId = Number(educationModality)

    if (VIRTUAL_MODALITIES.includes(modalityId)) {
        return 'virtual'
    }
    if (ONSITE_MODALITIES.includes(modalityId)) {
        return 'onSite'
    }
    if (BOTH_MODALITIES.includes(modalityId)) {
        return 'both'
    }

    return 'unknown'
}

const VIRTUAL_STUDY_MODALITY = 7 // Jornada virtual

// Si la modalidad final no está o no está mapeada, se usa la jornada como respaldo
export const resolveInterviewType = (
    educationModality: string | number | null | undefined,
    studyModality: string | number | null | undefined
): InterviewType => {
    const interviewType = getInterviewType(educationModality)

    if (interviewType !== 'unknown') {
        return interviewType
    }

    console.warn(
        `⚠️ Interview: educationModality "${educationModality}" sin mapeo, usando jornada "${studyModality}"`
    )

    if (
        studyModality === null ||
        studyModality === undefined ||
        studyModality === '' ||
        isNaN(Number(studyModality))
    ) {
        return 'unknown'
    }

    return Number(studyModality) === VIRTUAL_STUDY_MODALITY
        ? 'virtual'
        : 'onSite'
}
